import { ReactElement, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import { Link } from '@Hotels-at-Home/hah-fe-core/dist/components';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';

const SCALE_VALUE = 80;

const discoveryColors = ['#F2B826'];

const carouselData = [
  {
    id: 1,
    heading: 'discSlideHeading_1',
    description: 'discSlideDesc_1',
    productUrl: 'wobbleChess',
    siloImage: '/images/homepage/renaissance-hotels-wobble-chess.webp',
    lifestyleImage: '/images/homepage/renaissance-hotels-chess-board.webp',
    name: 'wobbleChess',
  },
];

const DiscoveriesSection = (): ReactElement => {
  const { t: tDiscoveries } = useTranslation('', { keyPrefix: '$pages.$home.$discoveriesSection' });
  const { t: tButton } = useTranslation('', { keyPrefix: '$common.$buttons' });
  const { t: tLink } = useTranslation('', {
    keyPrefix: '$local.$homePageLinks.discoveriesSection',
  });

  const [activeCarousel, setActiveCarousel] = useState(0);
  const [canvasDimensions, setCanvasDimensions] = useState({ width: 743, height: 665 });
  const [copyHeight, setCopyHeight] = useState(665);

  const containerRef = useRef<HTMLDivElement>(null);
  const discoveryItemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  const lifeImgRefs = useRef<(HTMLImageElement | null)[]>([]);

  const { contextSafe } = useGSAP({ scope: containerRef });
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    let resizeTimer: NodeJS.Timeout;

    const updateDimensions = () => {
      // Debounce resize to avoid too many updates
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (lifeImgRefs.current[0]) {
          const newWidth = lifeImgRefs.current[0].clientWidth;
          const newHeight = Math.floor(newWidth / 1.117);
          setCanvasDimensions({ width: newWidth, height: newHeight });
          setCopyHeight(newHeight);
        }
      }, 100);
    };

    // Initial dimensions
    if (lifeImgRefs.current[0]) {
      const newWidth = lifeImgRefs.current[0].clientWidth;
      const newHeight = Math.floor(newWidth / 1.117);
      setCanvasDimensions({ width: newWidth, height: newHeight });
      setCopyHeight(newHeight);
    }

    window.addEventListener('resize', updateDimensions);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  // Redraw the active canvas image when dimensions change
  useEffect(() => {
    if (isInitialized && canvasRefs.current[activeCarousel]) {
      const canvas = canvasRefs.current[activeCarousel];
      const ctx = canvas?.getContext('2d');

      if (ctx && canvas) {
        const img = new Image();
        img.src = carouselData[activeCarousel].lifestyleImage;

        // If image is already loaded (cached), draw immediately
        if (img.complete && img.naturalHeight !== 0) {
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        } else {
          // Otherwise wait for load
          img.onload = () => {
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          };
        }
      }
    }
  }, [canvasDimensions, activeCarousel, isInitialized]);

  const switchIn = contextSafe((inIndex: number) => {
    const item = discoveryItemRefs.current[inIndex];
    if (!item) return;

    const texts = item.querySelector('.discovery-copy > a > div:first-child') as HTMLElement;
    const products = item.querySelector('.discovery-product') as HTMLElement;
    const borderColor = item.querySelector('.border-color') as HTMLElement;
    const theCanvas = item.querySelector('.disc-canvas') as HTMLElement;
    const canvas = canvasRefs.current[inIndex];

    const tl = gsap.timeline();

    tl.set(item, { autoAlpha: 0, zIndex: 2 })
      .set(theCanvas, { autoAlpha: 0 })
      .set(borderColor, { autoAlpha: 0, left: 0, top: 0, bottom: 0, right: 0 })
      .to(item, { autoAlpha: 1, duration: 0.5 });

    // Handle canvas pixelation effect separately (async)
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Disable image smoothing for pixelation effect (all browser prefixes)
        ctx.imageSmoothingEnabled = false;
        (ctx as any).webkitImageSmoothingEnabled = false;
        (ctx as any).mozImageSmoothingEnabled = false;
        (ctx as any).msImageSmoothingEnabled = false;

        const cw = canvas.width;
        const ch = canvas.height;
        const img = new Image();
        const scale = { value: SCALE_VALUE };

        img.src = carouselData[inIndex].lifestyleImage;

        // Start canvas animation immediately, don't wait for onload
        tl.to(theCanvas, { autoAlpha: 1, duration: 0.5 });

        // When image loads, do the pixelation effect
        img.onload = () => {
          gsap.to(scale, {
            value: 1,
            duration: 0.4,
            ease: 'none',
            delay: 0.5,
            onUpdate: () => {
              // Calculate scaled dimensions (this creates the pixelation)
              const sw = Math.floor(cw / scale.value);
              const sh = Math.floor(ch / scale.value);

              // Draw image at small size, then scale it back up (creates pixel effect)
              ctx.drawImage(img, 0, 0, sw, sh);
              ctx.drawImage(canvas, 0, 0, sw, sh, 0, 0, cw, ch);
            },
          });
        };
      }
    }

    // Continue with main animations
    tl.to(borderColor, {
      autoAlpha: 1,
      left: '-5%',
      top: '-5%',
      bottom: '5%',
      right: '5%',
      duration: 0.5,
    })
      .from(texts, { y: -30, autoAlpha: 0, duration: 0.5, ease: 'power4.out' }, '-=1')
      .to(products, { autoAlpha: 1, x: 0, duration: 1, ease: 'power4.out' }, '-=0.4');
  });

  const switchOut = contextSafe((inIndex: number, outIndex: number) => {
    const outItem = discoveryItemRefs.current[outIndex];
    if (!outItem) return;

    const products = outItem.querySelector('.discovery-product') as HTMLElement;
    const borderColor = outItem.querySelector('.border-color') as HTMLElement;
    const theCanvas = outItem.querySelector('.disc-canvas') as HTMLElement;

    const tl = gsap.timeline();

    tl.set(outItem, { zIndex: 1 })
      .to(borderColor, { ease: 'none', left: 0, top: 0, bottom: 0, right: 0, duration: 0.3 })
      .to(outItem, { autoAlpha: 0, duration: 0.5 })
      .to(theCanvas, { autoAlpha: 0, duration: 0.5 }, '-=0.5')
      .to(products, { autoAlpha: 0, x: '30vw', duration: 0.5, ease: 'expo.out' }, '-=0.4')
      // Call switchIn with overlap - new item starts before old one finishes
      .call(() => switchIn(inIndex), [], '-=0.3')
      .set(theCanvas, { clearProps: 'all' })
      .set(products, { clearProps: 'all' });
  });

  // Initialize carousel - show the first item with canvas loaded
  useGSAP(
    () => {
      if (discoveryItemRefs.current.length === carouselData.length && !isInitialized) {
        // Small delay to ensure refs and images are loaded
        const timer = setTimeout(() => {
          // Set all items to hidden initially except the first one
          discoveryItemRefs.current.forEach((item, index) => {
            if (index === 0 && item) {
              gsap.set(item, { autoAlpha: 1, zIndex: 2 });

              // Set border and canvas for first item
              const borderColor = item.querySelector('.border-color') as HTMLElement;
              const theCanvas = item.querySelector('.disc-canvas') as HTMLElement;
              const products = item.querySelector('.discovery-product') as HTMLElement;
              const texts = item.querySelector(
                '.discovery-copy > a > div:first-child',
              ) as HTMLElement;
              const canvas = canvasRefs.current[0];

              if (borderColor) {
                gsap.set(borderColor, {
                  autoAlpha: 1,
                  left: '-5%',
                  top: '-5%',
                  bottom: '5%',
                  right: '5%',
                });
              }

              // Initialize the first product element to be visible
              if (products) {
                gsap.set(products, { autoAlpha: 1, x: 0 });
              }

              // Initialize the first text element to be visible
              if (texts) {
                gsap.set(texts, { autoAlpha: 1, y: 0 });
              }

              // Load and draw the first canvas image
              if (canvas && theCanvas) {
                gsap.set(theCanvas, { autoAlpha: 1 });
                const ctx = canvas.getContext('2d');
                if (ctx) {
                  const img = new Image();
                  img.src = carouselData[0].lifestyleImage;
                  img.onload = () => {
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                  };
                }
              }
            } else if (item) {
              gsap.set(item, { autoAlpha: 0, zIndex: 1 });
            }
          });
          setIsInitialized(true);
        }, 50);

        return () => clearTimeout(timer);
      }
      return undefined;
    },
    { scope: containerRef, dependencies: [isInitialized] },
  );

  const goToPrev = contextSafe(() => {
    if (!isInitialized) return;
    const outIndex = activeCarousel;
    const inIndex = outIndex > 0 ? outIndex - 1 : carouselData.length - 1;
    switchOut(inIndex, outIndex);
    setTimeout(() => setActiveCarousel(inIndex), 50);
  });

  const goToNext = contextSafe(() => {
    if (!isInitialized) return;
    const outIndex = activeCarousel;
    const inIndex = outIndex < carouselData.length - 1 ? outIndex + 1 : 0;
    switchOut(inIndex, outIndex);
    setTimeout(() => setActiveCarousel(inIndex), 50);
  });

  const handleKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  return (
    <section className="base-section discoveries-section hp-section" ref={containerRef}>
      <Link href={tLink('discoveries')} id="CTA-Discoveries">
        <div className="hp-top-text">
          <h2>
            <strong>{tDiscoveries('heading_1')}</strong>
            <span>{tDiscoveries('heading_2')}</span>
          </h2>
        </div>
        <picture>
          <img
            src="/images/homepage/renaissance-discoveries.webp"
            alt={tDiscoveries('alt')}
            width={1600}
            height={815}
            loading="lazy"
            decoding="async"
            fetchPriority="low"
          />
        </picture>
      </Link>

      <div className="carousel-wraper">
        <div className="carousel">
          <div className="discovery-wrap" style={{ height: `${copyHeight}px` }}>
            {carouselData.map((item, index) => (
              <div
                key={item.id}
                ref={el => {
                  discoveryItemRefs.current[index] = el;
                }}
                className="discovery-item"
                id={`item${item.id}`}
              >
                <div className="discovery-copy" style={{ height: `${copyHeight}px` }}>
                  <Link href={tLink(item.productUrl)}>
                    <div>
                      <h3 dangerouslySetInnerHTML={{ __html: tDiscoveries(item.heading) }} />
                      <p className="disc-txt">{tDiscoveries(item.description)}</p>
                      <p className="btn-main">
                        <span>{tButton('shopNow')}</span>
                      </p>
                    </div>
                    <div className="discovery-product">
                      <p>
                        <picture>
                          <img
                            src={item.siloImage}
                            alt={tDiscoveries(item.name)}
                            width={571}
                            height={331}
                            loading="lazy"
                            decoding="async"
                            fetchPriority="low"
                          />
                        </picture>
                      </p>
                    </div>
                  </Link>
                </div>
                <div className="discovery-lifestyle">
                  <p className="disc-canvas">
                    <canvas
                      ref={el => {
                        canvasRefs.current[index] = el;
                      }}
                      width={canvasDimensions.width}
                      height={canvasDimensions.height}
                    />
                  </p>
                  <p>
                    <picture className="border-img">
                      <img
                        ref={el => {
                          lifeImgRefs.current[index] = el;
                        }}
                        src={item.lifestyleImage}
                        alt={`Renaissance Hotels ${tDiscoveries(item.name)}`}
                        className="lifeImg"
                        width={748}
                        height={670}
                        loading="lazy"
                        decoding="async"
                        fetchPriority="low"
                      />
                    </picture>
                    <span className="border-color" style={{ background: discoveryColors[index] }} />
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {carouselData.length > 1 && (
        <div className="disc-arrows">
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div
            className="disc-arrows-left"
            onClick={goToPrev}
            onKeyDown={e => handleKeyDown(e, goToPrev)}
            role="button"
            tabIndex={0}
            aria-label="Previous discovery"
          >
            ❮
          </div>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div
            className="disc-arrows-right"
            onClick={goToNext}
            onKeyDown={e => handleKeyDown(e, goToNext)}
            role="button"
            tabIndex={0}
            aria-label="Next discovery"
          >
            ❯
          </div>
        </div>
      )}
    </section>
  );
};

export default DiscoveriesSection;
